// Frontend-input tools render an interactive UI FOR THE USER, not an internal
// tool-call trace. They bypass the debug-mode gate and the collapsible "Used tools"
// wrapper so the form is always visible.
//   - ask / ask_open   → inline card in the message stream
//   - request_commit_approval → sidebar Approve/Reject row (ADR-042)
// Keep in sync with the backend's FrontendInputTools.Names in
// src/backend/AiAssistant.Api/Services/Tools/FrontendInputTools.cs.
export const FRONTEND_INPUT_TOOL_NAMES: ReadonlySet<string> = new Set([
  "ask",
  "ask_open",
  "request_commit_approval",
]);

// Frontend-input tools that use the hybrid in-loop TCS wait (ADR-044).
// During streaming, messageStatus stays "running" for these tools so
// thread.isRunning remains true → composer stays disabled, no flash,
// and the user can't accidentally send a new message while the turn is
// still alive on the server. Excludes request_commit_approval (which uses
// the blocking body-bearing path and has its own status lifecycle).
export const HYBRID_WAIT_TOOL_NAMES: ReadonlySet<string> = new Set([
  "ask",
  "ask_open",
]);

export const WEB_SEARCH_TOOL_NAMES: ReadonlySet<string> = new Set([
  "web_search",
  "web_search_query",
  "web_open_page",
  "web_find_in_page",
]);

// Tools that are completely hidden from the process window timeline.
// These are internal housekeeping calls the user doesn't care about.
//   - upload_document_to_temp: staging plumbing for the document flow.
//   - praetor_get_case_id_by_case_number: resolves an opaque caseId from a case
//     number. Carries no user-relevant content — it's the lookup every other
//     case tool needs to turn a case number into an id. Hidden from the user
//     timeline; debug-mode users still see it in the tool-fallback ToolGroup
//     trace (which does not filter this set).
export const HIDDEN_TOOL_NAMES: ReadonlySet<string> = new Set([
  "upload_document_to_temp",
  "praetor_get_case_id_by_case_number",
]);

// Sub-agent orchestration tools. The parent calls these but they render as a
// sub-agent-lane UiPart rather than individual tool-call entries.
export const SUB_AGENT_TOOL_NAMES: ReadonlySet<string> = new Set([
  "research",
  "continue_research",
]);

// ─── Document Generation Flow Tools ─────────────────────────────────────────────
// Tools that participate in the document generation pipeline. When any of these
// appear in a message's tool-call parts, the DocumentStepper replaces the generic
// ProcessWindow for that message.

export const DOCUMENT_FLOW_TOOL_NAMES: ReadonlySet<string> = new Set([
  "praetor_list_templates",
  "praetor_get_template_skill",
  "praetor_get_case_document_edit_skill",
  "praetor_get_temp_doc_edit_skill",
  "praetor_apply_edits",
  "request_commit_approval",
  "commit_draft",
  "save_revision",
  "save_to_thread_generated_documents",
]);


// ─── Document Edit Flow (V2 — intent-descriptive) ────────────────────────────────
// Maps the three get_*_skill methods to their edit flow variants.
export type DocEditVariant = "template" | "case-edit" | "re-edit";
export type DocEditPhaseKey = "preparing" | "editing" | "reviewing" | "saving";

export const TOOL_TO_DOC_EDIT_VARIANT: Record<string, DocEditVariant> = {
  praetor_get_template_skill: "template",
  praetor_get_case_document_edit_skill: "case-edit",
  praetor_get_temp_doc_edit_skill: "re-edit",
};

export const DOC_EDIT_PHASE_TOOLS: Record<
  DocEditPhaseKey,
  ReadonlySet<string>
> = {
  preparing: new Set([
    "praetor_get_template_skill",
    "praetor_get_case_document_edit_skill",
    "praetor_get_temp_doc_edit_skill",
    "praetor_list_templates",
  ]),
  editing: new Set(["praetor_apply_edits"]),
  reviewing: new Set(["request_commit_approval"]),
  saving: new Set([
    "commit_draft",
    "save_revision",
    "save_to_thread_generated_documents",
  ]),
};

// All tools that participate in the edit flow (used for detection)
export const DOC_EDIT_ALL_TOOLS: ReadonlySet<string> = new Set([
  ...DOC_EDIT_PHASE_TOOLS.preparing,
  ...DOC_EDIT_PHASE_TOOLS.editing,
  ...DOC_EDIT_PHASE_TOOLS.reviewing,
  ...DOC_EDIT_PHASE_TOOLS.saving,
]);
